import { subject } from "@casl/ability";
import { PaginationState, SortingState } from "@tanstack/react-table";
import {
  FirestoreDataConverter,
  Timestamp,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";
import { firestore } from "@/base";
import { getCurrentUser } from "@/services/auth";
import { generateQuery } from "@/utils/firestore";
import { colaboradorConverter } from "../colaboradores/firestore";
import { getActiveLicenciadaId } from "../users/store";

const DEFAULT_LIMIT = 10;
export const DEFAULT_SORTING: SortingState = [{ id: "razaoSocial", desc: false }];
export const DEFAULT_PAGINATION: PaginationState = { pageIndex: 0, pageSize: DEFAULT_LIMIT };

const convertToDate = (date: unknown): Date => {
  if (!date) return new Date();
  if (date instanceof Timestamp) {
    return date.toDate();
  }
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === "string" || typeof date === "number") {
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }
  // Lida com objetos serializados do Firestore que podem vir de Functions
  if (typeof date === "object" && "seconds" in date && typeof (date as any).seconds === "number") {
    return new Timestamp((date as any).seconds, (date as any).nanoseconds || 0).toDate();
  }
  return new Date(); // Retorno padrão
};

export type TLicenciadaDatabaseFields = Pick<TLicenciada, "createdBy" | "updatedBy"> & {
  createdAt: ReturnType<typeof serverTimestamp>;
  updatedAt?: ReturnType<typeof serverTimestamp>;
  deletedAt: ReturnType<typeof serverTimestamp> | null;
};
export type TLicenciadaFormFields = Omit<
  TLicenciada,
  "id" | "refPath" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
> &
  Partial<Pick<TLicenciada, "id" | "refPath">>;
export type TLicenciadaForm = TLicenciadaFormFields & TLicenciadaDatabaseFields;

// export type TLicenciadaDatabaseFields = Pick<TLicenciada, "createdBy" | "updatedBy"> & {
//   createdAt: ReturnType<typeof serverTimestamp>;
//   updatedAt?: ReturnType<typeof serverTimestamp>;
//   deletedAt: ReturnType<typeof serverTimestamp> | null;
// };
// export type TLicenciadaFormFields = Omit<
//   TLicenciada,
//   "id" | "refPath" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
// > &
//   Partial<Pick<TLicenciada, "id" | "refPath">>;
// export type TLicenciadaForm = TLicenciadaFormFields & TLicenciadaDatabaseFields;

type TLicenciadaDocument = Omit<
  TLicenciada,
  "createdAt" | "updatedAt" | "deletedAt" | "contratoInicio" | "contratoFim"
> & {
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  deletedAt?: Timestamp | null;
  contratoInicio?: Timestamp;
  contratoFim?: Timestamp;
};

const licenciadaConverter: FirestoreDataConverter<TLicenciada> = {
  toFirestore(data) {
    const dataToSave = { ...data };

    if (dataToSave.contratoInicio instanceof Date) {
      dataToSave.contratoInicio = Timestamp.fromDate(dataToSave.contratoInicio);
    }
    if (dataToSave.contratoFim instanceof Date) {
      dataToSave.contratoFim = Timestamp.fromDate(dataToSave.contratoFim);
    }

    delete (dataToSave as Partial<TLicenciada>).id;
    delete (dataToSave as Partial<TLicenciada>).refPath;

    return dataToSave;
  },
  fromFirestore(snap) {
    const { createdAt, updatedAt, contratoInicio, contratoFim, ...document } = snap.data() as TLicenciadaDocument;

    const data: TLicenciada = subject("TLicenciada", {
      ...document,
      id: snap.id,
      createdAt: convertToDate(createdAt),
      contratoInicio: convertToDate(contratoInicio),
      contratoFim: convertToDate(contratoFim),
      refPath: snap.ref.path
    });

    if (updatedAt) {
      data.updatedAt = convertToDate(updatedAt);
    }
    return data;
  }
};

/**
 * Get all licenciadas collection reference
 * @returns - licenciadas collection reference
 */
function getLicenciadasCollectionReference() {
  return collection(firestore, `licenciadas`);
}
// prettier-ignore
/**
 * Get all licenciadas collection reference where the current user is the owner
 * @returns - licenciadas collection reference
 */
// function queryMylicenciadasCollectionReference() {
//   const licenciadasCollectionReference = getLicenciadasCollectionReference();
//   return query(
//     licenciadasCollectionReference,
//     and(where("deletedAt", "==", null), where("ativo", "==", true))
//   );
// }

/**
 * Get all licenciadas collection reference where the current user is the owner
 * @param uid - User id
 * @param role - role
 * @returns - licenciadas collection reference
 */
// prettier-ignore
// function queryMylicenciadasCollectionNewReference(uid: string, role: string) {
//    const licenciadasCollectionReference = getLicenciadasCollectionReference();

//    if (role === "ADMIN") {
//     return query(licenciadasCollectionReference);
//    }

//   return query(
//     licenciadasCollectionReference,
//     or(where("createdBy", "==", uid), where("colaboradores", "array-contains", uid))
//   );
// }

/**
 * Get a licenciada document reference
 * @param licenciadaId - licenciada id
 * @returns - licenciada document reference
 */
export function getLicenciadaDocumentReference(licenciadaId: TLicenciada["id"] | undefined) {
   if (!licenciadaId) {
    return undefined;
  }
  const licenciadasCollectionReference = getLicenciadasCollectionReference().withConverter(licenciadaConverter);
  return doc(licenciadasCollectionReference, licenciadaId);
}

/**
 * Get a licenciada document reference
 * @returns - licenciada document reference
 */
export function getActiveLicenciadaDocumentReference() {
  const uid = getActiveLicenciadaId();
  if (!uid) {
    return null;
  }
  const licenciadasCollectionReference = getLicenciadasCollectionReference().withConverter(licenciadaConverter);
  return doc(licenciadasCollectionReference, uid);
}

/**
 * Get a licenciada document reference from path
 * @param licenciadaPath - licenciada path
 * @returns - licenciada document reference
 */
// function getLicenciadaDocumentReferenceFromPath(licenciadaPath: string) {
//   return doc(firestore, licenciadaPath).withConverter(licenciadaConverter);
// }

/**
 * Get a licenciada by cnpj
 * @param cnpj - licenciada cnpj
 * @returns - Promise with the licenciada snapshot
 * @throws - Error if the licenciada is already registered
 */
// async function getLicenciadaByCnpj(cnpj: string) {
//   const licenciadasCollectionReference = getLicenciadasCollectionReference();
//   const licenciadaQuery = query(licenciadasCollectionReference, where("cnpj", "==", cnpj));
//   const { docs } = await getDocs(licenciadaQuery);
//   return docs[0] && licenciadaConverter.fromFirestore(docs[0]);
// }

/**
 * Add a licenciada to the database
 * @param licenciada - licenciada to add
 * @returns - Promise with the licenciada reference
 */
// function addlicenciada(licenciada: Omit<TLicenciadaForm, "id" | "refPath">) {
//   const licenciadasCollectionReference = getLicenciadasCollectionReference();
//   return addDoc(licenciadasCollectionReference, licenciada);
// }

/**
 * Add a licenciada to the database
 * @param licenciada - licenciada to add
 * @returns - Promise with the licenciada reference
 */
// async function addlicenciadaWithConverter(licenciada: Omit<TLicenciadaForm, "id" | "refPath">) {
//   const licenciadasCollectionReference = getLicenciadasCollectionReference();

//   const licenciadaWithSameCNPJ = await getLicenciadaByCnpj(licenciada.cnpj);
//   if (licenciadaWithSameCNPJ) {
//     throw new Error("licenciada já cadastrada");
//   }

//   const licenciadaCreated = await addDoc(licenciadasCollectionReference, licenciada);
//   return await getDoc(licenciadaCreated.withConverter(licenciadaConverter));
// }

/**
 * Update a licenciada to the database
 * @param licenciadaId - licenciada id
 * @param licenciada - licenciada to update
 * @returns - Promise with the licenciada reference
 */
// export function updatelicenciada(licenciadaId: TLicenciada["id"], licenciada: TLicenciadaForm) {
//   const licenciadaDocumentReference = getLicenciadaDocumentReference(licenciadaId);
//   if (!licenciadaDocumentReference) {
//     throw new Error(`Licenciada document reference not found for id ${licenciadaId}`);
//   }
//   const licenciadaDocumentReferenceWithConverter = licenciadaDocumentReference.withConverter(licenciadaConverter);
//   const currentUserId = getCurrentUser()?.uid;
//   return updateDoc(licenciadaDocumentReferenceWithConverter, {
//     ...licenciada,
//     updatedAt: serverTimestamp(),
//     updatedBy: currentUserId
//   });
// }

/**
 * Delete a licenciada to the database
 * @param licenciada - licenciada to delete
 * @returns - Promise with the licenciada reference
 */
// export function deletelicenciada(licenciada: TLicenciada) {
//   const licenciadaDocumentReference = getLicenciadaDocumentReference(licenciada.id);
//   if (!licenciadaDocumentReference) {
//     throw new Error(`Licenciada document reference not found for id ${licenciada.id}`);
//   }
//   return deleteDoc(licenciadaDocumentReference);
// }

/**
 * Get a licenciada by id
 * @param licenciadaId - licenciada id
 * @returns - Promise with the licenciada snapshot
 */
// export function getLicenciadaById(licenciadaId: TLicenciada["id"]) {
//   const licenciadaDocumentReference = getLicenciadaDocumentReference(licenciadaId);
//   if (!licenciadaDocumentReference) {
//     throw new Error(`Licenciada document reference not found for id ${licenciadaId}`);
//   }
//   return getDoc(licenciadaDocumentReference);
// }

/**
 * Get all licenciadas from the database
 * @returns - Promise with all licenciadas
 */
// export function getLicenciadas() {
//   const querylicenciadasCollectionReference =
//     queryMylicenciadasCollectionReference().withConverter(licenciadaConverter);
//   return getDocs(querylicenciadasCollectionReference);
// }

// prettier-ignore
/**
 * Get all licenciadas from the database
 * @param uid - User id
 * @param role - role
 * @param root0 - Query options
 * @param root0.sorting - Sorting options
 * @param root0.filters - Filters options
 * @param root0.pagination - Pagination options
 * @param root0.paginationCursors - Array of pagination cursors (startAt, endAt)[]
 * @returns - Promise with all licenciadas
 */
// export function getPaginatedlicenciadas(
//   uid: string,
//   role = "",
//   {
//     sorting = [],
//     filters = [],
//     pagination = { pageIndex: 0, pageSize: DEFAULT_LIMIT },
//     paginationCursors,
//   }: TPaginatedQueryOptions<TLicenciada> = {}
// ): Promise<TPaginatedQueryResponse<TLicenciada>> {
//   const querylicenciadasCollectionReference = queryMylicenciadasCollectionNewReference(uid, role).withConverter(licenciadaConverter);
//   const qAll = generateQuery(querylicenciadasCollectionReference, { sorting, filters });
//   const qPaginated = generateQuery(querylicenciadasCollectionReference, {
//     sorting,
//     filters,
//     pagination,
//     paginationCursors
//   });

//   // eslint-disable-next-line compat/compat
//   return Promise.all([getDocs(qPaginated), getCountFromServer(qAll)]);
// }

/**
 * Get all licenciadas collection reference
 * @returns - licenciadas collection reference with converter
 */
function queryAllLicenciadasCollectionReference() {
  const licenciadasCollectionReference = getLicenciadasCollectionReference();
  return query(licenciadasCollectionReference);
}

// prettier-ignore
/**
 * Get all licenciadas from the database with pagination
 * @param root0 - Query options
 * @param root0.sorting - Sorting options
 * @param root0.filters - Filters options
 * @param root0.pagination - Pagination options
 * @param root0.paginationCursors - Array of pagination cursors (startAt, endAt)[]
 * @returns - Promise with all licenciadas
 */
export function getPaginatedLicenciadas(
  {
    sorting = [],
    filters = [],
    pagination = { pageIndex: 0, pageSize: DEFAULT_LIMIT },
    paginationCursors,
  }: TPaginatedQueryOptions<TLicenciada> = {}
): Promise<TPaginatedQueryResponse<TLicenciada>> {
  const queryLicenciadasCollectionReference = queryAllLicenciadasCollectionReference().withConverter(licenciadaConverter);
  const qAll = generateQuery(queryLicenciadasCollectionReference, { sorting, filters });
  const qPaginated = generateQuery(queryLicenciadasCollectionReference, {
    sorting,
    filters,
    pagination,
    paginationCursors
  });

  // eslint-disable-next-line compat/compat
  return Promise.all([getDocs(qPaginated), getCountFromServer(qAll)]);
}

/**
 * Update a licenciada in the database
 * @param licenciadaId - licenciada id
 * @param licenciada - licenciada to update
 * @returns - Promise with the licenciada reference
 */
export function updateLicenciada(licenciadaId: TLicenciada["id"], licenciada: TLicenciadaForm) {
  const licenciadaDocumentReference = getLicenciadaDocumentReference(licenciadaId);
  if (!licenciadaDocumentReference) {
    throw new Error(`Licenciada document reference not found for id ${licenciadaId}`);
  }
  const licenciadaDocumentReferenceWithConverter = licenciadaDocumentReference.withConverter(licenciadaConverter);
  const currentUserId = getCurrentUser()?.uid;
  return updateDoc(licenciadaDocumentReferenceWithConverter, {
    ...licenciada,
    updatedAt: serverTimestamp(),
    updatedBy: currentUserId
  });
}

/**
 *
 * @param licenciadaId
 * @param theme
 */
export function updateLicenciadaTheme(licenciadaId: TLicenciada["id"], theme: TLicenciada["theme"]) {
  const licenciadaDocumentReference = getLicenciadaDocumentReference(licenciadaId);
  if (!licenciadaDocumentReference) {
    throw new Error(`Licenciada document reference not found for id ${licenciadaId}`);
  }
  const licenciadaDocumentReferenceWithConverter = licenciadaDocumentReference.withConverter(licenciadaConverter);
  const currentUserId = getCurrentUser()?.uid;
  return updateDoc(licenciadaDocumentReferenceWithConverter, {
    theme,
    updatedAt: serverTimestamp(),
    updatedBy: currentUserId
  });
}

/**
 * Get config document with empresaMaster
 * @returns - Promise with the config data
 */
export async function getConfig(): Promise<TConfigGeral | null> {
  const configDocumentReference = doc(firestore, "config", "geral");
  const configSnapshot = await getDoc(configDocumentReference);
  if (!configSnapshot.exists()) {
    return null;
  }
  return configSnapshot.data() as TConfigGeral;
}

/**
 * Get all colaboradores from empresaMaster
 * @param empresaPath - Empresa master path (e.g., "empresas/id" or "licenciadas/id/empresas/id")
 * @returns - Promise with all colaboradores
 */
export async function getColaboradoresFromEmpresaMaster(empresaPath: string): Promise<TColaborador[]> {
  // Se for um caminho completo, usar diretamente; senão, assumir que é da collection empresas
  const empresaDocumentReference = empresaPath.includes("/")
    ? doc(firestore, empresaPath)
    : doc(firestore, "empresas", empresaPath);

  const colaboradoresCollectionReference = collection(empresaDocumentReference, "colaboradores").withConverter(
    colaboradorConverter
  );
  const q = query(colaboradoresCollectionReference, where("deletedAt", "==", null));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((documentSnap) => documentSnap.data());
}

/**
 * Update adminLicenciada field of a colaborador
 * @param empresaPath - Empresa path (e.g., "empresas/id" or "licenciadas/id/empresas/id")
 * @param colaboradorId - Colaborador id
 * @param adminLicenciada - New value for adminLicenciada
 * @returns - Promise
 */
export function updateColaboradorAdminLicenciada(empresaPath: string, colaboradorId: string, adminLicenciada: boolean) {
  // Se for um caminho completo, usar diretamente; senão, assumir que é da collection empresas
  const empresaDocumentReference = empresaPath.includes("/")
    ? doc(firestore, empresaPath)
    : doc(firestore, "empresas", empresaPath);

  const colaboradorDocumentReference = doc(empresaDocumentReference, "colaboradores", colaboradorId);
  const currentUserId = getCurrentUser()?.uid;
  return updateDoc(colaboradorDocumentReference, {
    adminLicenciada,
    updatedAt: serverTimestamp(),
    updatedBy: currentUserId
  });
}
